import React from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Three-tier per-seat pricing display: Solo (1) / Team (2-5) / Scale (6+).
 * `theme="dark"` for the LandingPage/PricingPage dark sections,
 * `theme="light"` for marketing surfaces on white backgrounds.
 */
const TIERS = [
  {
    id: 'solo', label: 'Solo', seats: '1 seat',
    monthly: 35, annual: 350,
    description: 'For independent agents getting started.',
    features: ['Unlimited leads', 'Kanban pipeline + bulk ops', 'AI voice notes', 'Email tracking', 'Calendar + reminders', 'Priority email support'],
  },
  {
    id: 'team', label: 'Team', seats: '2 – 5 seats', highlight: true,
    monthly: 30, annual: 300,
    description: 'For small teams sharing leads.',
    features: ['Everything in Solo, plus:', 'Shared lead pool', 'Lead requests workflow', 'Role-based permissions', 'Audit log', 'Team analytics dashboard'],
  },
  {
    id: 'scale', label: 'Scale', seats: '6+ seats',
    monthly: 25, annual: 250,
    description: 'Best value for growing organizations.',
    features: ['Everything in Team, plus:', 'Seamless.AI sourcing', 'Conversion funnel reports', 'Custom assignment rules', 'Webhooks + integrations', 'White-glove onboarding'],
  },
];

export function PricingTiers({ theme = 'dark', defaultCycle = 'monthly', ctaLabel = 'Start free trial', ctaTo = '/register' }) {
  const [cycle, setCycle] = React.useState(defaultCycle);
  const isDark = theme === 'dark';

  // Shared color tokens
  const cardBase = isDark
    ? 'bg-[#141414] border-white/10'
    : 'bg-white border-slate-200';
  const cardHighlight = isDark
    ? 'border-[#007AFF]/40 shadow-[0_0_40px_rgba(0,122,255,0.15)]'
    : 'border-[#0047AB]/60 shadow-[0_10px_30px_-15px_rgba(0,71,171,0.3)]';
  const headingClass = isDark ? 'text-white' : 'text-slate-900';
  const seatClass = isDark ? 'text-zinc-500' : 'text-slate-500';
  const featureClass = isDark ? 'text-zinc-300' : 'text-slate-700';
  const blueIcon = isDark ? 'text-[#007AFF]' : 'text-[#0047AB]';
  const buttonHighlight = isDark
    ? 'bg-[#007AFF] hover:bg-[#005bb5] text-white'
    : 'bg-[#0047AB] hover:bg-[#003580] text-white';
  const buttonSecondary = isDark
    ? 'bg-white/10 hover:bg-white/15 text-white'
    : 'bg-slate-900 hover:bg-slate-800 text-white';
  const togglePill = isDark ? 'bg-white/5 border-white/10' : 'bg-slate-100 border-slate-200';
  const toggleActive = isDark ? 'bg-[#007AFF] text-white' : 'bg-[#0047AB] text-white';
  const toggleInactive = isDark ? 'text-zinc-400' : 'text-slate-600';

  return (
    <div data-testid="pricing-tiers">
      {/* Billing cycle toggle */}
      <div className="flex justify-center mb-10">
        <div className={`inline-flex p-1 rounded-full border ${togglePill}`}>
          <button
            type="button"
            onClick={() => setCycle('monthly')}
            className={`px-5 py-2 text-xs font-medium uppercase tracking-wider rounded-full transition-all ${
              cycle === 'monthly' ? toggleActive : toggleInactive
            }`}
            data-testid="cycle-monthly-toggle"
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => setCycle('annual')}
            className={`px-5 py-2 text-xs font-medium uppercase tracking-wider rounded-full transition-all inline-flex items-center gap-1.5 ${
              cycle === 'annual' ? toggleActive : toggleInactive
            }`}
            data-testid="cycle-annual-toggle"
          >
            Annual
            <span className="text-[9px] bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded-full font-semibold">Save 17%</span>
          </button>
        </div>
      </div>

      {/* Tier cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto">
        {TIERS.map((t) => {
          const price = cycle === 'annual' ? t.annual : t.monthly;
          const period = cycle === 'annual' ? '/seat/yr' : '/seat/mo';
          return (
            <div
              key={t.id}
              className={`relative rounded-2xl border ${cardBase} ${t.highlight ? cardHighlight : ''} p-7`}
              data-testid={`pricing-tier-${t.id}`}
            >
              {t.highlight && (
                <span className={`absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.15em] px-3 py-1 rounded-full ${isDark ? 'bg-[#007AFF] text-white' : 'bg-[#0047AB] text-white'}`}>
                  <Sparkles className="w-3 h-3" /> Most popular
                </span>
              )}
              <p className={`text-[10px] font-bold uppercase tracking-[0.25em] ${blueIcon} mb-2`}>{t.label}</p>
              <p className={`text-xs ${seatClass} mb-4`}>{t.seats}</p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className={`text-5xl font-bold tracking-tighter ${headingClass}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                  ${price}
                </span>
                <span className={`${seatClass} text-sm`}>{period}</span>
              </div>
              <p className={`text-xs ${seatClass} mb-6 leading-relaxed`}>{t.description}</p>
              <Link to={ctaTo} className="block mb-6">
                <Button
                  className={`w-full rounded-full h-11 font-semibold text-sm transition-all ${t.highlight ? buttonHighlight : buttonSecondary}`}
                  data-testid={`tier-cta-${t.id}`}
                >
                  {ctaLabel} <ArrowRight className="ml-1.5 w-3.5 h-3.5" />
                </Button>
              </Link>
              <ul className="space-y-2.5">
                {t.features.map((f) => (
                  <li key={f} className={`flex items-start gap-2 text-xs ${featureClass} leading-relaxed`}>
                    <Check className={`w-3.5 h-3.5 ${blueIcon} mt-0.5 shrink-0`} />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      <p className={`text-center text-xs ${seatClass} mt-8`}>
        Per-seat pricing automatically tiers as your team grows. 7-day free trial, no credit card required.
      </p>
    </div>
  );
}
